import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import {
  type ActiveDataTable,
  type ClosedDataTable,
  type InspectionState,
} from "./InspectionsLists.d";
import { useAxios } from "@/hooks/use-axios/useAxios";
import { useDevice } from "@/store/useDevice";
import InspectionsListHeader from "./inspections-lists-header/InspectionsListsHeader";
import InspectionsTable from "./inspections-table/InspectionsTable";
import InspectionCreator from "../inpsection-creator/InspectionCreator";
import routes from "@/assets/routes/routes";
import ProblemMessage from "@/components/problem-message/ProblemMessage";
import InspectionsMobileList from "./inspections-mobile-list/InspectionsMobileList";
import { STATUS } from "@/assets/constants/constants";

const InspectionsLists = () => {
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const lastTableRef = useRef<InspectionState>("active");
  const isMobile = useDevice((state) => state.isMobile);

  const { request } = useAxios();
  const [error, setError] = useState<string>("");
  const [activeTable, setActiveTable] = useState<InspectionState>("active");
  const [inspections, setInspections] = useState<
    ActiveDataTable | ClosedDataTable
  >({ data: [] });
  const [inspectionCreator, setInspectionCreator] = useState<boolean>(false);
  const [lastFilteredSearch, setLastFilteredSearch] = useState<string>("");
  const [newPagination, setNewPagination] = useState(false);

  const getActiveInspections = useCallback(
    async (page: number = 0) => {
      setLastFilteredSearch("");
      if (newPagination) setNewPagination(false);
      try {
        const filter = `?status=${STATUS.IN_PROGRESS}&size=${
          isMobile ? "10" : "20"
        }&page=${page}`;
        const result = await request({
          url: `${routes.getInspections}${filter}`,
        });
        if (result) {
          if (error) setError("");
          setActiveTable("active");
          const newResults = {
            pages: result.totalPages,
            page: result.number,
            data: result.content.map((inspection: any) => {
              return {
                id: inspection.id,
                createdAt: inspection.createdAt,
                engineName: inspection.engineName,
                snStand: inspection.nsStand,
                esnIn: inspection.esnIn,
                colour: inspection.colour,
                phase: {
                  id: inspection.phase[0].phaseId,
                  date: inspection.phase[0].startDate,
                  name: inspection.phase[0].phaseName,
                },
                pdf: inspection.pdf,
              };
            }),
          };
          setInspections(newResults);
          startAutoReload("active", page, true);
        }
      } catch {
        setError("noPossibleInformation");
        startAutoReload("active", 0, true);
      }
    },
    [error, newPagination, request, isMobile],
  );

  const getClosedInspections = useCallback(
    async (page: number = 0) => {
      setLastFilteredSearch("");
      if (newPagination) setNewPagination(false);
      try {
        const filter = `?status=${STATUS.CLOSED}&size=${
          isMobile ? "10" : "20"
        }&page=${page}`;
        const result = await request({
          url: `${routes.getInspections}${filter}`,
        });

        if (result) {
          if (error) setError("");
          setActiveTable("closed");
          const newResults = {
            pages: result.totalPages,
            page: result.number,
            data: result.content.map((inspection: any) => {
              return {
                id: inspection.id,
                createdAt: inspection.createdAt,
                endDate: inspection.endDate,
                engineName: inspection.engineName,
                snStand: inspection.nsStand,
                esnOut: inspection.esnOut,
                colour: inspection.colour,
                pdf: inspection.pdf,
              };
            }),
          };
          setInspections(newResults);
          startAutoReload("closed", page, true);
        }
      } catch {
        setError("noPossibleInformation");
        startAutoReload("closed", 0, true);
      }
    },
    [error, lastFilteredSearch, newPagination, request, isMobile],
  );

  const filterOptions = useCallback(
    async (params: string) => {
      try {
        startAutoReload(null, 0, false);
        const url = `${routes.getInspections}${params}`;
        setLastFilteredSearch(url);
        const result = await request({
          url: `${url}${0}`,
        });
        if (result) {
          if (error) setError("");
          setNewPagination(true);
          const areActivesInspectios = url.includes("status=IN_PROGRESS");
          const newResults = {
            pages: result.totalPages,
            page: result.number,
            data: result.content.map((inspection: any) => {
              if (areActivesInspectios) {
                return {
                  id: inspection.id,
                  createdAt: inspection.createdAt,
                  engineName: inspection.engineName,
                  snStand: inspection.nsStand,
                  esnIn: inspection.esnIn,
                  colour: inspection.colour,
                  phase: {
                    id: inspection.phase[0].phaseId,
                    date: inspection.phase[0].startDate,
                    name: inspection.phase[0].phaseName,
                  },
                  pdf: inspection.pdf,
                };
              }
              return {
                id: inspection.id,
                createdAt: inspection.createdAt,
                endDate: inspection.endDate,
                engineName: inspection.engineName,
                snStand: inspection.nsStand,
                esnOut: inspection.esnOut,
                colour: inspection.colour,
                pdf: inspection.pdf,
              };
            }),
          };
          setInspections(newResults);
        }
      } catch {
        setError("noPossibleInformation");
        setLastFilteredSearch("");
      }
    },
    [error, request],
  );

  const startAutoReload = useCallback(
    (type: InspectionState | null, page: number, canReload: boolean) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      if (type !== null && canReload) {
        lastTableRef.current = type;
        timerRef.current = setTimeout(() => {
          if (lastTableRef.current === "active") {
            getActiveInspections(page);
          } else {
            getClosedInspections(page);
          }
        }, 60000);
      }
    },
    [getActiveInspections, getClosedInspections],
  );

  useEffect(() => {
    getActiveInspections();
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const lastPageNumber = useMemo(() => inspections?.page, [inspections]);

  const changeTablePage = useCallback(
    async (
      page: number,
      status: InspectionState | undefined,
      notFromFiltered?: boolean | undefined,
    ) => {
      if (newPagination) setNewPagination(false);
      if (lastFilteredSearch.trim().length === 0 || notFromFiltered) {
        const newStatus = status || activeTable;
        if (newStatus === "active") getActiveInspections(page);
        else getClosedInspections(page);
      } else {
        try {
          const url = `${lastFilteredSearch}${page}`;
          const result = await request({ url: url });
          if (result) {
            if (error) setError("");
            const areActivesInspectios = url.includes("?status=IN_PROGRESS");
            const newResults = {
              pages: result.totalPages,
              page: result.number,
              data: result.content.map((inspection: any) => {
                if (areActivesInspectios) {
                  return {
                    id: inspection.id,
                    createdAt: inspection.createdAt,
                    engineName: inspection.engineName,
                    snStand: inspection.nsStand,
                    esnIn: inspection.esnIn,
                    colour: inspection.colour,
                    phase: {
                      id: inspection.phase[0].phaseId,
                      date: inspection.phase[0].startDate,
                      name: inspection.phase[0].phaseName,
                    },
                    pdf: inspection.pdf,
                  };
                }
                return {
                  id: inspection.id,
                  createdAt: inspection.createdAt,
                  endDate: inspection.endDate,
                  engineName: inspection.engineName,
                  snStand: inspection.nsStand,
                  esnOut: inspection.esnOut,
                  colour: inspection.colour,
                  pdf: inspection.pdf,
                };
              }),
            };
            setInspections(newResults);
          }
        } catch {
          setError("noPossibleInformation");
          setLastFilteredSearch("");
        }
      }
    },
    [
      activeTable,
      error,
      getActiveInspections,
      getClosedInspections,
      lastFilteredSearch,
      newPagination,
      request,
    ],
  );

  const changeTable = useCallback(
    (status: InspectionState) => {
      if (status !== activeTable) {
        changeTablePage(0, status, true);
      }
    },
    [changeTablePage, activeTable],
  );

  const openOrCloseInspectorCreator = useCallback(() => {
    const newCreatorState = !inspectionCreator;
    if (newCreatorState) {
      startAutoReload(activeTable, 0, false);
      setInspectionCreator(true);
    } else {
      setInspectionCreator(false);
      if (lastFilteredSearch.trim().length > 0) {
        changeTablePage(lastPageNumber as number, activeTable);
      } else if (activeTable === "active") {
        getActiveInspections(lastPageNumber);
      } else {
        getClosedInspections(lastPageNumber);
      }
    }
  }, [
    activeTable,
    changeTablePage,
    getActiveInspections,
    getClosedInspections,
    inspectionCreator,
    lastFilteredSearch,
    lastPageNumber,
    startAutoReload,
  ]);

  const getNoSortedInspections = useCallback(() => {
    if (activeTable === "active") getActiveInspections();
    else getClosedInspections();
  }, [activeTable, getActiveInspections, getClosedInspections]);

  return (
    <div className="grid grid-rows-[auto_1fr] h-full min-h-0 max-w-full">
      <div className="shrink-0">
        <InspectionsListHeader
          addInspection={openOrCloseInspectorCreator}
          activeTable={activeTable}
          chargeTable={changeTable}
        />
      </div>
      {isMobile ? (
        <InspectionsMobileList
          activeList={activeTable}
          listData={inspections}
          changePage={changeTablePage}
          searchInspections={filterOptions}
          getNoSortedInspections={getNoSortedInspections}
          cleanSearch={() => setLastFilteredSearch("")}
          addInspection={openOrCloseInspectorCreator}
          openedCreator={inspectionCreator}
          newPagination={newPagination}
          lastFiltered={lastFilteredSearch}
        />
      ) : (
        <InspectionsTable
          tableType={activeTable}
          tableData={inspections}
          changePage={changeTablePage}
          searchInspections={filterOptions}
          cleanSearch={() => setLastFilteredSearch("")}
          newPagination={newPagination}
          lastFiltered={lastFilteredSearch}
          getNoSortedInspections={getNoSortedInspections}
        />
      )}
      {error?.length > 0 ? (
        <ProblemMessage closeMessage={() => setError("")} error={error} />
      ) : null}
      {inspectionCreator ? (
        <InspectionCreator closeCreator={openOrCloseInspectorCreator} />
      ) : null}
    </div>
  );
};

export default InspectionsLists;
